import { defineModule } from '../../engine/module';

// The feedback module's UI (docs/modules/feedback.md). Everything here runs on the engine —
// the triage list, the report detail (screenshots arrive in the built-in Attachments tab,
// every status change in Activity) and the area catalogue behind the dialog's dropdown.
//
// The reporting dialog itself is NOT a screen: it is the engine's FeedbackButton, rendered in
// each skin's top-bar (the "?" icon) so "report a problem" is reachable from every screen.

const TRIAGE_COLUMNS = ['ref', 'title', 'kind', 'severity', 'status', 'priority', 'area_id', 'route_path', 'vote_count', 'submitted_by_name', 'assigned_user_id', 'created_at'];
const TRIAGE_FILTERS = [
  { field: 'status', kind: 'chips' as const, counts: true },
  { field: 'kind', kind: 'chips' as const },
  { field: 'severity', kind: 'select' as const, label: 'Severity' },
  { field: 'area_id', kind: 'select' as const, label: 'Area' },
  { field: 'assigned_user_id', kind: 'select' as const, label: 'Owner' },
];

export const feedbackModule = defineModule({
  name: 'feedback',
  nav: [
    {
      label: 'Organisation',
      area: 'config',
      order: 10,
      items: [
        { label: 'Feedback', to: '/feedback', icon: 'message-square', key: 'f', permission: 'feedback:list' },
        { label: 'Feedback areas', to: '/feedback/areas', icon: 'folder', permission: 'feedback-areas:list' },
      ],
    },
  ],
  screens: [
    {
      path: '/feedback',
      resource: 'feedback',
      kind: 'list',
      title: 'Feedback',
      subtitle: 'What people hit while using the platform. Chips narrow, views persist, and every report carries the screen it came from.',
      columns: TRIAGE_COLUMNS,
      filters: TRIAGE_FILTERS,
      query: { sort: '-created_at', expand: ['area', 'assigned_user'] },
      detailPath: '/feedback/:id',
      actions: [{ name: 'areas', label: 'Areas', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/feedback/areas') }],
    },
    {
      // 'areas' registers before ':id' so the static route wins over the id pattern.
      path: '/feedback/areas',
      resource: 'feedback-areas',
      kind: 'list',
      title: 'Feedback areas',
      subtitle: 'The "which part of the platform?" dropdown. A route prefix pre-selects the area for anyone reporting from under that path.',
      columns: ['label', 'key', 'route_prefix', 'module', 'sort_order', 'is_active'],
      filters: [{ field: 'is_active', kind: 'toggle', label: 'Active only' }],
      query: { sort: 'sort_order' },
      form: { fields: ['label', 'key', 'route_prefix', 'module', 'sort_order', 'is_active', 'description'] },
      inspector: true,
    },
    {
      path: '/feedback/:id',
      resource: 'feedback',
      kind: 'detail',
      title: 'Feedback',
      builtinTabs: ['overview', 'attachments', 'notes', 'activity'],
    },
  ],
  commands: [
    { id: 'feedback-report', label: 'Report feedback', kind: 'create', icon: 'message-square', keywords: 'bug issue problem screenshot idea suggestion', shortcut: 'mod+shift+f', run: ({ close }) => {
      close();
      // The top-bar button owns the dialog; the palette just presses it.
      document.querySelector<HTMLButtonElement>('.hf-feedback-btn')?.click();
    } },
    { id: 'feedback-list', label: 'Feedback', kind: 'go to', icon: 'message-square', keywords: 'reports bugs ideas triage', run: ({ navigate }) => navigate('/feedback') },
    { id: 'feedback-areas', label: 'Feedback areas', kind: 'go to', icon: 'list', keywords: 'dropdown screens configuration', run: ({ navigate }) => navigate('/feedback/areas') },
  ],
});
